import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useProfessionalSessionStore } from '@/stores/ProfessionalSessionStore'
import { observerApiService } from '@/httpClient/observerApiService'
import { isSelectedClientUserId } from '@/types/observer.types'

/**
 * Route guard for all observer routes.
 * Validates professional session, exchanges auth codes, enforces session status.
 * Runs a proactive policy check on first load to catch stale/revoked access early.
 * No hostname check needed - this guard only exists in the observer build.
 */

let accessValidated = false

export async function observerGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  // Allow routes that don't require auth (session-ended, restricted)
  if (!to.meta.requiresProfessionalAuth) {
    return next()
  }

  const sessionStore = useProfessionalSessionStore()

  // Check for auth code in URL (one-time code from Portal)
  // Portal handoff pattern: ?token={jwt}&refreshToken={rt}&clientId={parentUserId}
  // Legacy/backward-compat: ?code={token}&parentId={clientId}
  const token =
    (to.query.token as string | undefined) ||
    (to.query.code as string | undefined)
  const refreshToken = to.query.refreshToken as string | undefined
  const clientId =
    (to.query.clientId as string | undefined) ||
    (to.query.parentId as string | undefined)
  const clientName = to.query.clientName as string | undefined
  const clientEmail = to.query.clientEmail as string | undefined

  if (token && clientId) {
    // A non-positive client id can never resolve to a valid observed parent.
    if (!isSelectedClientUserId(clientId)) {
      return next({
        name: 'observer-session-ended',
        query: { reason: 'error' }
      })
    }

    const success = await sessionStore.initializeFromAuthCode(token, clientId)

    if (success) {
      // Store refresh token if provided
      if (refreshToken) {
        sessionStore.setRefreshToken(refreshToken)
      }

      // Store client display info from handoff params (immediate display)
      if (clientName) {
        sessionStore.observedParentName = clientName
      }
      if (clientEmail) {
        sessionStore.observedParentEmail = clientEmail
      }

      // Fetch authoritative client info from API (non-blocking)
      sessionStore.fetchClientInfo()

      // Reset validation flag for new session
      accessValidated = false

      // Strip handoff params from URL
      const cleanQuery = { ...to.query }
      const handoffParams = [
        'token',
        'refreshToken',
        'clientId',
        'code',
        'parentId',
        'clientName',
        'clientEmail',
        'returnTo'
      ] as const
      handoffParams.forEach((param) => delete cleanQuery[param])

      const returnTo = to.query.returnTo
      const returnPath =
        typeof returnTo === 'string' && returnTo.startsWith('/')
          ? returnTo
          : undefined

      if (returnPath) {
        return next({ path: returnPath, replace: true })
      }

      return next({ ...to, query: cleanQuery, replace: true })
    }

    // Auth code exchange failed
    return next({ name: 'observer-session-ended', query: { reason: 'error' } })
  }

  // If session is active, run one-time access validation then allow
  if (sessionStore.isProfessionalSession) {
    if (!accessValidated && sessionStore.selectedClientUserId) {
      accessValidated = true
      // Fire-and-forget in non-DEV: if policy denies, the 403 interceptor navigates
      if (!import.meta.env.DEV) {
        observerApiService.validateClientAccess(
          sessionStore.selectedClientUserId
        )
      }
      // Refresh client info if not already populated (e.g. page refresh)
      if (
        !sessionStore.observedParentName ||
        !sessionStore.observedCoparentName
      ) {
        sessionStore.fetchClientInfo()
      }
    }
    return next()
  }

  // No token in URL - try dev bypass if in DEV mode
  if (import.meta.env.DEV) {
    const devSuccess = await sessionStore.initializeFromAuthCode(
      'dev-bypass',
      '99001'
    )
    if (devSuccess) {
      return next()
    }
  }

  // Check if session exists but is not active
  if (sessionStore.sessionStatus === 'suspended') {
    return next({ name: 'observer-restricted' })
  }

  if (
    ['revoked', 'expired', 'ended', 'error'].includes(
      sessionStore.sessionStatus
    )
  ) {
    return next({
      name: 'observer-session-ended',
      query: { reason: sessionStore.sessionStatus }
    })
  }

  // No session and no auth code - redirect to session-ended
  return next({ name: 'observer-session-ended', query: { reason: 'expired' } })
}
